import React from "react";
import withLanguageContext from "lib/hocs/withLanguageContext";
import { Box } from "Components/Base/Layout/Box";
import BaseGrid from "Components/Fragment/BaseGrid";
import { Typography } from "Components/Base/DataDisplay/Typography";
import {
  LearningActivityCard,
  LearningActivityListItem,
} from "Components/Module/LearningActivityItem";
import ActivityDescription from "Components/Module/LearningActivityItem/Description";
import {
  getCardImage,
  useRating,
} from "Components/Module/LearningActivityItem/functions";
import Rating from "lib/components/Rating";
import { CLOCK } from "Components/Icons";
import { POST } from "Common/api";
import { useDisplayLearningActivityCardImage } from "lib/hocs/useLearningActivityCardState";
import { OnlineCourseChips } from "./Chips";
import { formatDuration, getButtonText } from "../../functions";
import { OnlineCourseActions } from "./Actions";
import { useMyLearning } from "../../context";

const OnlineItem = ({
  course,
  isManagerOrAdmin,
  languageContext,
  basePath,
}) => {
  const { removeCourse, listView } = useMyLearning();
  const { rating, comment, updateRating } = useRating(course);
  const displayImage = useDisplayLearningActivityCardImage();

  const { imageComponent } = getCardImage(
    course,
    displayImage,
    false,
    listView ? "small" : "medium",
  );

  const unregisterFromCourse = async () => {
    if (window.confirm(languageContext("unregisterFromOnlineCourseConfirm"))) {
      await POST(
        "/v1/learning/onlinecourses/:courseId/unregister",
        { courseId: course.courseId },
        {},
      );
      removeCourse(course.courseId);
    }
  };

  const buttonText = getButtonText(course, languageContext);

  const onlineCourseURL = course.munioURL
    ? course.munioURL
    : `${basePath}education/entercourse.aspx?from=ml&courseid=${course.courseId}`;

  const time = formatDuration(
    course.durationHours,
    course.durationMinutes,
    languageContext,
  );

  const OnlineItemType = listView
    ? LearningActivityListItem
    : LearningActivityCard;

  return (
    <OnlineItemType
      id={`learningActivityCard${course.courseId}`}
      title={course.title}
      type={course.courseType}
      displayImage={displayImage}
      imageComponent={imageComponent}
      chips={
        <OnlineCourseChips
          course={course}
          completed={course.completed}
          languageContext={languageContext}
        />
      }
      content={
        <>
          <BaseGrid childWidth="auto">
            {course.allowRating && (
              <Rating
                currentRating={rating || 0}
                currentComment={comment || ""}
                type={course.courseType}
                objectId={course.courseId}
                updateRating={updateRating}
                readOnly={false}
                managerView={isManagerOrAdmin}
              />
            )}
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography variant="h4" sx={{ fontWeight: "normal" }}>
                {languageContext("onlinecourse")}
              </Typography>

              {time && (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <CLOCK />
                  {time}
                </Box>
              )}
            </Box>
          </BaseGrid>

          <ActivityDescription
            description={course.description}
            maxLength={listView ? 10000 : 100}
          />
        </>
      }
      actions={
        <OnlineCourseActions
          course={course}
          isManagerOrAdmin={isManagerOrAdmin}
          onlineCourseURL={onlineCourseURL}
          unregisterFromCourse={unregisterFromCourse}
          buttonText={buttonText}
          languageContext={languageContext}
        />
      }
    />
  );
};

export default withLanguageContext(OnlineItem, "ActivityCentre");
